import { Button, styled } from '@mui/material';
import { PDFViewerPageChrome, PDFViewerViewMode } from '../PDFViewer.props';

// US Letter aspect ratio (11/8.5 ≈ 1.294) for placeholder sizing
const PAGE_ASPECT_RATIO = 1.294;

export const PdfViewerPageContainer = styled('div')<{
  $viewMode: PDFViewerViewMode;
  $pageChrome: PDFViewerPageChrome;
  $isVisible: boolean;
  $isSelected: boolean;
  $width?: number;
}>(({ $viewMode, $pageChrome, $isVisible, $isSelected, $width }) => {
  const usesFloatingChrome = $viewMode === PDFViewerViewMode.DOCUMENT && $pageChrome === 'floating' && !$isSelected;
  const isDocument = $viewMode === PDFViewerViewMode.DOCUMENT;
  const floatingPageBorder = '1px solid rgba(148, 163, 184, 0.28)';
  const pageBorder = $isSelected ? '3px solid #1976d2' : '1px solid #ddd';

  return {
    backgroundColor: usesFloatingChrome ? '#FFFFFF' : undefined,
    border: usesFloatingChrome ? floatingPageBorder : pageBorder,
    borderRadius: usesFloatingChrome ? '2px' : '8px',
    boxShadow: usesFloatingChrome ? '0 18px 45px rgba(15, 23, 42, 0.12)' : '0 2px 4px rgba(0,0,0,0.1)',
    marginBottom: usesFloatingChrome ? 0 : '20px',
    overflow: 'hidden',
    scrollMarginTop: usesFloatingChrome ? '64px' : undefined,
    minHeight: (() => {
      if ($viewMode === PDFViewerViewMode.DOCUMENT && !$isVisible) {
        return $width ? `${Math.round($width * PAGE_ASPECT_RATIO)}px` : '400px';
      }
      return 'auto';
    })(),
    width: $viewMode === PDFViewerViewMode.THUMBNAIL || isDocument ? $width : undefined,
    display: $viewMode === PDFViewerViewMode.THUMBNAIL ? 'flex' : undefined,
    flexDirection: $viewMode === PDFViewerViewMode.THUMBNAIL ? 'column' : undefined,
    opacity: 0,
    transform: 'translateY(20px) scale(0.98)',
    transition:
      'opacity 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    '&.animate-in': {
      opacity: 1,
      transform: 'translateY(0) scale(1)',
    },
    '& .textLayer': {
      cursor: 'text',
      userSelect: 'text',
    },
    '& .textLayer :is(span, br)': {
      userSelect: 'text',
    },
    '& canvas': {
      display: 'block',
    },
  };
});

export const PdfViewerPageHeader = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  backgroundColor: '#f8f9fa',
  padding: '8px 16px',
  borderBottom: '1px solid #ddd',
  fontSize: '14px',
  fontWeight: 'bold',
  color: '#495057',
});

export const HighlightableTextOverlayActionButton = styled(Button)(() => ({
  position: 'fixed',
  zIndex: 9999,
  background: 'white',
  border: '1px solid #ccc',
  borderRadius: '4px',
  padding: '6px 12px',
  fontSize: '14px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
  cursor: 'pointer',
  color: 'black',
}));

export const CustomTextLayerWrapper = styled('div', {
  shouldForwardProp: prop => prop !== '$showNativeSelection' && prop !== '$showCopyFeedback',
})<{
  $showCopyFeedback?: boolean;
  $showNativeSelection?: boolean;
}>(({ $showCopyFeedback = false, $showNativeSelection = false }) => {
  const nativeSelectionBackground = $showCopyFeedback ? 'rgba(34, 197, 94, 0.34)' : 'rgba(3, 125, 232, 0.28)';

  return {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    pointerEvents: 'all',
    userSelect: 'text',
    WebkitUserSelect: 'text',
    // The invisible per-word spans stay selectable for browser copy/find. Citation
    // contexts hide native paint and use HighlightLayer; read-only preview keeps a
    // subtle native selection paint because there is no citation overlay/action.
    '& [data-word-id]': {
      userSelect: 'text',
      WebkitUserSelect: 'text',
    },
    '& ::selection': {
      background: $showNativeSelection ? nativeSelectionBackground : 'transparent',
      color: 'transparent',
    },
    '& ::-moz-selection': {
      background: $showNativeSelection ? nativeSelectionBackground : 'transparent',
      color: 'transparent',
    },
  };
});
